import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Moon, Sun, User, Building2, Bell, Shield, Loader2, Check, X, Eye, EyeOff, MapPin, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-fetch";
import { IS_LOCAL_DEMO } from "@/components/demo/DemoPackageSwitch";

export default function SettingsPage() {
  const { user, token, updateToken } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Profile state
  const [name, setName] = useState(user?.name || "");
  const [profileDirty, setProfileDirty] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Organization state
  const [orgName, setOrgName] = useState<string | null>(null);
  const [orgNameOriginal, setOrgNameOriginal] = useState<string | null>(null);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgMsg, setOrgMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const isAdmin = user?.features?.includes("admin");

  // Password state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Sync name when user changes
  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  // Fetch org info
  useEffect(() => {
    if (!token) return;
    apiFetch("/api/me", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.organization) {
          setOrgName(data.organization.name || "Unknown");
          setOrgNameOriginal(data.organization.name || "Unknown");
          setPlanName(data.organization.plan || "Free");
        } else {
          setOrgName("Not set");
          setOrgNameOriginal("Not set");
          setPlanName("--");
        }
      })
      .catch(() => {
        setOrgName("Error loading");
        setPlanName("--");
      });
  }, [token]);

  const handleProfileSave = async () => {
    if (!token || !name.trim()) return;
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const res = await apiFetch("/api/me", {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setProfileMsg({ type: "error", text: data.error || "Failed to update profile" });
        return;
      }
      if (data.token) {
        updateToken(data.token);
      }
      setProfileDirty(false);
      setProfileMsg({ type: "success", text: "Profile updated" });
      setTimeout(() => setProfileMsg(null), 3000);
    } catch {
      setProfileMsg({ type: "error", text: "Failed to update profile" });
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordMsg(null);
    if (newPassword.length < 8) {
      setPasswordMsg({ type: "error", text: "New password must be at least 8 characters" });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordMsg({ type: "error", text: "Passwords do not match" });
      return;
    }
    setPasswordSaving(true);
    try {
      const res = await apiFetch("/api/me/change-password", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPasswordMsg({ type: "error", text: data.error || "Failed to change password" });
        return;
      }
      setPasswordMsg({ type: "success", text: "Password changed successfully" });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordMsg(null);
      }, 2000);
    } catch {
      setPasswordMsg({ type: "error", text: "Failed to change password" });
    } finally {
      setPasswordSaving(false);
    }
  };

  if (IS_LOCAL_DEMO) return (
    <div className="space-y-6 max-w-3xl">
      <div><h1 className="text-2xl font-semibold text-foreground">Settings</h1><p className="text-sm text-muted-foreground mt-1">Demo profile and workspace preferences</p></div>
      <section className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-5"><User size={18} className="text-forest-500" /><h2 className="text-lg font-semibold text-foreground">Profile preview</h2></div>
        <dl className="grid sm:grid-cols-2 gap-5 text-sm"><div><dt className="text-xs text-muted-foreground mb-1">Name</dt><dd>{user?.name || "Loading..."}</dd></div><div><dt className="text-xs text-muted-foreground mb-1">Email</dt><dd>{user?.email || "Loading..."}</dd></div><div><dt className="text-xs text-muted-foreground mb-1">Workspace</dt><dd>{orgName ?? "Loading..."}</dd></div><div><dt className="text-xs text-muted-foreground mb-1">Role</dt><dd className="capitalize">{user?.role || "Demo administrator"}</dd></div></dl>
        <p className="mt-5 text-xs text-muted-foreground">Account changes and invitations are outside this demo.</p>
      </section>
      <section className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4"><Sun size={18} className="text-forest-500" /><h2 className="text-lg font-semibold text-foreground">Appearance</h2></div>
        <div className="flex items-center justify-between gap-4"><div><p className="text-sm font-medium">Theme</p><p className="text-xs text-muted-foreground mt-1">Choose light or dark for this browser</p></div><button onClick={toggleTheme} className="flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted">{theme === "light" ? <Moon size={16} /> : <Sun size={16} />}Switch to {theme === "light" ? "Dark" : "Light"}</button></div>
      </section>
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <User size={18} className="text-forest-500" />
          <h2 className="text-lg font-semibold text-foreground">Profile</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setProfileDirty(e.target.value !== user?.name);
              }}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email</label>
            <input
              type="email"
              value={user?.email || ""}
              readOnly
              className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-muted-foreground cursor-not-allowed"
            />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={handleProfileSave}
            disabled={!profileDirty || profileSaving || !name.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            {profileSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Save Changes
          </button>
          {profileMsg && (
            <span className={`text-sm font-medium ${profileMsg.type === "success" ? "text-green-500" : "text-red-400"}`}>
              {profileMsg.text}
            </span>
          )}
        </div>
      </div>

      {/* Organization */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Building2 size={18} className="text-forest-500" />
          <h2 className="text-lg font-semibold text-foreground">Organization</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Organization Name
            </label>
            {isAdmin ? (
              <input
                type="text"
                value={orgName ?? "Loading..."}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50"
              />
            ) : (
              <input
                type="text"
                value={orgName ?? "Loading..."}
                readOnly
                className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-muted-foreground cursor-not-allowed"
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Plan</label>
            <input
              type="text"
              value={planName ?? "Loading..."}
              readOnly
              className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-muted-foreground cursor-not-allowed"
            />
          </div>
        </div>
        {orgName && orgName.toLowerCase().includes("soil seed and water") && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Locations</p>
              <button
                type="button"
                disabled
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-dashed border-border text-[11px] text-muted-foreground cursor-not-allowed"
              >
                <Plus size={11} />
                Add location
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="inline-flex items-start gap-2 px-3 py-2 rounded-lg border border-border bg-background/60">
                <div className="mt-0.5">
                  <MapPin size={12} className="text-forest-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">Parker Dairy</p>
                  <p className="text-[11px] text-muted-foreground">
                    Congress, AZ · Primary dairy compost site
                  </p>
                </div>
              </div>
              <div className="inline-flex items-start gap-2 px-3 py-2 rounded-lg border border-border bg-background/60">
                <div className="mt-0.5">
                  <MapPin size={12} className="text-forest-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">19th Ave Yard</p>
                  <p className="text-[11px] text-muted-foreground">
                    Phoenix, AZ · Main yard & office
                  </p>
                </div>
              </div>
            </div>
            <p className="mt-2 text-[10px] text-muted-foreground">
              Location management is visual-only here for now; we&apos;ll wire this up to multi-site permissions and routing.
            </p>
          </div>
        )}
        {isAdmin && orgName !== orgNameOriginal && orgName?.trim() && (
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={async () => {
                if (!token || !orgName?.trim()) return;
                setOrgSaving(true);
                setOrgMsg(null);
                try {
                  const res = await apiFetch("/api/organization", {
                    method: "PATCH",
                    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                    body: JSON.stringify({ name: orgName.trim() }),
                  });
                  if (!res.ok) {
                    const data = await res.json();
                    setOrgMsg({ type: "error", text: data.error || "Failed to update" });
                    return;
                  }
                  setOrgNameOriginal(orgName.trim());
                  setOrgMsg({ type: "success", text: "Organization updated" });
                  setTimeout(() => setOrgMsg(null), 3000);
                } catch {
                  setOrgMsg({ type: "error", text: "Failed to update" });
                } finally {
                  setOrgSaving(false);
                }
              }}
              disabled={orgSaving}
              className="flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {orgSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save Organization
            </button>
            {orgMsg && (
              <span className={`text-sm font-medium ${orgMsg.type === "success" ? "text-green-500" : "text-red-400"}`}>
                {orgMsg.text}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Appearance */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          {theme === "light" ? (
            <Sun size={18} className="text-forest-500" />
          ) : (
            <Moon size={18} className="text-forest-500" />
          )}
          <h2 className="text-lg font-semibold text-foreground">Appearance</h2>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Theme</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Choose between light and dark mode
            </p>
          </div>
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-4 py-2 bg-secondary border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            {theme === "light" ? (
              <>
                <Moon size={16} />
                Switch to Dark
              </>
            ) : (
              <>
                <Sun size={16} />
                Switch to Light
              </>
            )}
          </button>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Bell size={18} className="text-forest-500" />
          <h2 className="text-lg font-semibold text-foreground">Notifications</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Email notifications for composting alerts. These will be configurable per-organization in a future update.
        </p>
        <div className="space-y-4">
          {[
            { label: "Temperature alerts", desc: "Get notified when a windrow drops below 131\u00B0F" },
            { label: "Turn reminders", desc: "Receive reminders when turns are overdue" },
            { label: "Low stock alerts", desc: "Get notified when inventory drops below reorder thresholds" },
          ].map(({ label, desc }) => (
            <div key={label} className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
              <div className="w-10 h-6 rounded-full flex items-center bg-forest-600 justify-end cursor-default">
                <div className="w-4 h-4 rounded-full bg-white mx-1 shadow" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Security */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Shield size={18} className="text-forest-500" />
          <h2 className="text-lg font-semibold text-foreground">Security</h2>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Change Password</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Update your account password
            </p>
          </div>
          <button
            onClick={() => {
              setShowPasswordModal(true);
              setPasswordMsg(null);
              setCurrentPassword("");
              setNewPassword("");
              setConfirmNewPassword("");
            }}
            className="px-4 py-2 bg-secondary border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
          >
            Change
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-foreground">Change Password</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="p-1 rounded hover:bg-secondary text-muted-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {passwordMsg && (
              <div className={`rounded-lg px-4 py-3 text-sm mb-4 ${
                passwordMsg.type === "success"
                  ? "bg-green-500/10 border border-green-500/20 text-green-400"
                  : "bg-red-500/10 border border-red-500/20 text-red-400"
              }`}>
                {passwordMsg.text}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPw ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2.5 pr-10 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPw ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="w-full px-3 py-2.5 pr-10 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 placeholder:text-muted-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordChange}
                  disabled={passwordSaving || !currentPassword || !newPassword || !confirmNewPassword}
                  className="flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {passwordSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Change Password
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
